import React from "react";
import { useForm } from "react-hook-form";
import { useLoaderData, useLocation } from "react-router";
import { useSearchParams } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";
import invariant from "tiny-invariant";

import AdminView from "./components/AdminView/TournamentForm";
import TDView from "./components/TDView";
import { TournamentProvider } from "./contexts/Tournament";
import { tournamentFormSchema } from "./lib/tournamentFormSchema";

import {
    selectAgeLevels,
    selectBracketTypes,
} from "../../../../features/global/globalSlice";
import { selectSelectedTournament } from "../../../../features/tourneymgmt/tournamentManagementSlice";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { AUTH_LEVELS } from "../../../../lib/globals";
import { isChildTournament } from "../../../../lib/utils";
import { IAPIResponse, ITeam, IUser } from "../../../../types/api";
import { ITourneyMgmtTourneyInfoData } from "../../../../types/forms";

const Tournament = (): React.JSX.Element => {
    const location = useLocation();
    const user = useAppSelector(state => state.auth.user);
    invariant(user);

    const tournaments = useAppSelector(
        state => state.tournamentmanagement.tournaments,
    );

    const ageLevels = useAppSelector(selectAgeLevels);
    const brackets = useAppSelector(selectBracketTypes);

    const isCreatingTournament =
        /tournament-management\/tournament\/add\/?$/.test(location.pathname);

    const tournament = isCreatingTournament
        ? undefined
        : useAppSelector(state => selectSelectedTournament(state));

    const isMasterTournament = !isChildTournament(tournament);

    const { teams, users: tournamentDirectors } = useLoaderData() as {
        teams:
            | (IAPIResponse & {
                  teams: ITeam[];
              })
            | undefined;
        users: IUser[];
    };

    const methods = useForm<ITourneyMgmtTourneyInfoData>({
        mode: "onChange",
        resolver: yupResolver(tournamentFormSchema),
        defaultValues: {
            startDate: tournament?.startDate
                ? new Date(tournament.startDate)
                : undefined,
            endDate: tournament?.endDate
                ? new Date(tournament.endDate)
                : undefined,
            ageLevels: isMasterTournament
                ? tournaments.length
                    ? tournaments
                          .filter(t => t.parent === tournament?.id)
                          .map(t => {
                              return {
                                  tournamentId: t.id,
                                  ageLevel: {
                                      value: t.ageLevel
                                          ? ageLevels.find(
                                                a => a.ageLevel === t.ageLevel,
                                            )?.id
                                          : 0,
                                      label: t.ageLevel ? t.ageLevel : "",
                                  },
                                  maxTeams: t.maxTeams ? t.maxTeams : 0,
                                  cost: t.cost || 0,
                                  bracket: {
                                      value: t.playoffBracket?.id || 0,
                                      label: t.playoffBracket
                                          ? brackets.find(
                                                b =>
                                                    b.bracketId ===
                                                    t.playoffBracket?.id,
                                            )?.name
                                          : "",
                                  },
                              };
                          })
                    : undefined
                : tournament
                  ? [
                        {
                            ageLevel: {
                                value: tournament.ageLevel
                                    ? ageLevels.find(
                                          a =>
                                              a.ageLevel ===
                                              tournament.ageLevel,
                                      )?.id
                                    : 0,
                                label: tournament.ageLevel
                                    ? tournament.ageLevel
                                    : "",
                            },
                            maxTeams: tournament.maxTeams
                                ? tournament.maxTeams
                                : 0,
                            cost: tournament.cost ? tournament.cost : 0,
                            bracket: {
                                value: tournament.playoffBracket?.id
                                    ? tournament.playoffBracket.id
                                    : 0,
                                label: tournament.playoffBracket
                                    ? brackets.find(
                                          b =>
                                              b.bracketId ===
                                              tournament.playoffBracket?.id,
                                      )?.name
                                    : "",
                            },
                        },
                    ]
                  : undefined,
        },
    });

    return (
        <TournamentProvider
            methods={methods}
            tournament={tournament}
            isCreatingTournament={isCreatingTournament}
            tournamentDirectors={tournamentDirectors}
            teams={teams}
        >
            <section className="tournamentInfo">
                {AUTH_LEVELS.sitemanagement.includes(user.userLevel) ? (
                    <AdminView />
                ) : (
                    <TDView />
                )}
            </section>
        </TournamentProvider>
    );
};

export default Tournament;
